'use client';

import { useMemo } from 'react';
import { EChart, type EChartsOption } from '@/components/charts/EChart';
import {
  PiggyBankIcon,
  ShieldIcon,
  CreditCardIcon,
  SparkleIcon,
  InfoIcon,
} from '@phosphor-icons/react';
import { useFormatCurrency } from '@/hooks/useFormatCurrency';
import { token } from '@/lib/theme/tokens';
import { useThemeVersion } from '@/lib/theme/useThemeColors';
import type { GoalType } from '@/types';
import { projectSavings, projectDebt } from './projection';
import { formatYmShort } from './helpers';

function formatCompactBRL(v: number): string {
  const abs = Math.abs(v);
  if (abs >= 1_000_000) {
    const n = v / 1_000_000;
    return `R$ ${Number.isInteger(n) ? n.toFixed(0) : n.toFixed(1)}M`;
  }
  if (abs >= 1_000) {
    const n = v / 1_000;
    return `R$ ${Number.isInteger(n) ? n.toFixed(0) : n.toFixed(1)}k`;
  }
  return `R$ ${v.toFixed(0)}`;
}

const MONTHS_FORWARD = 36;

interface Props {
  goalType: GoalType;
  name: string;
  targetAmount: number;
  monthlyContribution: number;
  startingBalance: number;
  reserveMonths: number;
  essentialMonthly: number;
  debtBalance: number;
  debtPayment: number;
  debtAnnualRate: number;
}

function monthLabel(monthsFromToday: number): string {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() + monthsFromToday);
  return formatYmShort(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
}

function HowItWorks({ goalType }: { goalType: GoalType }) {
  const config = {
    Savings: {
      Icon: PiggyBankIcon,
      bg: 'bg-success-surface',
      color: 'text-success-text',
      title: 'Como funciona — Poupança',
      bullets: [
        'Defina um valor alvo e uma data desejada.',
        'Informe o aporte mensal planejado para acompanhar a saúde da meta.',
        'Vincule contas ou investimentos: o saldo deles passa a contar como progresso.',
      ],
    },
    Reserve: {
      Icon: ShieldIcon,
      bg: 'bg-info-surface',
      color: 'text-info-text',
      title: 'Como funciona — Reserva',
      bullets: [
        'O valor alvo é calculado como meses × despesas essenciais do seu orçamento.',
        'Despesas essenciais somam moradia, alimentação, transporte fixo e contas básicas.',
        'Vincule contas líquidas (corrente, poupança, CDB de liquidez diária).',
      ],
    },
    DebtPayoff: {
      Icon: CreditCardIcon,
      bg: 'bg-error-surface',
      color: 'text-error-text',
      title: 'Como funciona — Dívida',
      bullets: [
        'O saldo devedor cresce com juros mensais (taxa anual ÷ 12).',
        'Seu pagamento mensal cobre os juros e amortiza o principal.',
        'Progresso = saldo inicial − saldo atual. Pague mais que os juros para evoluir.',
      ],
    },
  } as const;

  const c = config[goalType];
  const Icon = c.Icon;

  return (
    <div className="rounded-2xl border border-border-subtle bg-surface p-4 shadow-none">
      <div className="mb-4 flex items-center justify-between">
        <span className="text-2xs font-bold tracking-widest text-text-tertiary uppercase">
          Pré-visualização
        </span>
        <div className={`size-10 rounded-xl flex items-center justify-center ${c.bg} ${c.color}`}>
          <Icon className="size-5" weight="bold" />
        </div>
      </div>
      <h2 className="mb-3 text-lg font-bold text-text-primary">{c.title}</h2>
      <ul className="space-y-2.5">
        {c.bullets.map((b) => (
          <li key={b} className="flex gap-2 text-sm/relaxed text-text-secondary">
            <span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-muted" />
            <span>{b}</span>
          </li>
        ))}
      </ul>
      <div className="mt-4 flex items-start gap-2 border-t border-border-subtle pt-3 text-xs text-text-tertiary">
        <InfoIcon size={14} className="mt-0.5 shrink-0" />
        <p>Preencha os campos ao lado para ver a trajetória projetada aqui.</p>
      </div>
    </div>
  );
}

/** Just the chart + caption — no halo card wrapper. Reusable in any preview surface. */
export function ProjectionInline(props: Props) {
  const {
    goalType,
    targetAmount,
    monthlyContribution,
    startingBalance,
    reserveMonths,
    essentialMonthly,
    debtBalance,
    debtPayment,
    debtAnnualRate,
  } = props;
  const formatCurrency = useFormatCurrency();
  const themeVersion = useThemeVersion();
  const isDebt = goalType === 'DebtPayoff';

  const derivedTarget = useMemo(() => {
    if (goalType === 'Reserve') return reserveMonths * essentialMonthly;
    if (goalType === 'DebtPayoff') return debtBalance;
    return targetAmount;
  }, [goalType, reserveMonths, essentialMonthly, debtBalance, targetAmount]);

  const monthlyForProjection = isDebt ? debtPayment : monthlyContribution;

  const canProject = useMemo(() => {
    if (isDebt) return Number.isFinite(debtBalance) && debtBalance > 0;
    if (goalType === 'Reserve') {
      return (reserveMonths > 0 && essentialMonthly > 0) || monthlyContribution > 0 || startingBalance > 0;
    }
    return targetAmount > 0 || monthlyContribution > 0 || startingBalance > 0;
  }, [isDebt, goalType, debtBalance, reserveMonths, essentialMonthly, monthlyContribution, targetAmount, startingBalance]);

  const data = useMemo(() => {
    if (!canProject) return [] as Array<{ monthIndex: number; label: string; base: number }>;
    const start = isDebt ? debtBalance : startingBalance;
    const future = isDebt
      ? projectDebt(start, debtPayment, debtAnnualRate, MONTHS_FORWARD)
      : projectSavings(start, monthlyForProjection, MONTHS_FORWARD);

    const TAIL = 3;
    let cutoff = MONTHS_FORWARD;
    if (isDebt) {
      const idx = future.findIndex((v) => v <= 0);
      if (idx >= 0) cutoff = Math.min(MONTHS_FORWARD, idx + 1 + TAIL);
    } else if (derivedTarget > 0) {
      const idx = future.findIndex((v) => v >= derivedTarget);
      if (idx >= 0) cutoff = Math.min(MONTHS_FORWARD, idx + 1 + TAIL);
    }

    const points: Array<{ monthIndex: number; label: string; base: number }> = [
      { monthIndex: 0, label: monthLabel(0), base: start },
    ];
    for (let i = 0; i < cutoff; i++) {
      points.push({ monthIndex: i + 1, label: monthLabel(i + 1), base: future[i] });
    }
    return points;
  }, [canProject, isDebt, debtBalance, debtPayment, debtAnnualRate, startingBalance, monthlyForProjection, derivedTarget]);

  const xTickIndices = useMemo(() => {
    if (data.length === 0) return [];
    const last = data[data.length - 1].monthIndex;
    const step = last > 18 ? 6 : last > 9 ? 3 : last > 4 ? 2 : 1;
    const ticks: number[] = [];
    for (let v = 0; v <= last; v += step) ticks.push(v);
    if (ticks[ticks.length - 1] !== last) ticks.push(last);
    return ticks;
  }, [data]);

  const completionMonth = useMemo(() => {
    if (!canProject || data.length === 0) return null;
    if (isDebt) {
      const idx = data.findIndex((p) => p.monthIndex > 0 && p.base <= 0);
      return idx > 0 ? data[idx].monthIndex : null;
    }
    if (derivedTarget <= 0) return null;
    const idx = data.findIndex((p) => p.monthIndex > 0 && p.base >= derivedTarget);
    return idx > 0 ? data[idx].monthIndex : null;
  }, [canProject, data, isDebt, derivedTarget]);

  const option = useMemo<EChartsOption>(() => {
    if (data.length === 0) return {};
    const colorTarget = token('--warning', '#f59e0b');
    const colorLine = token('--success', '#059669');
    const colorAxis = token('--border', '#e2e8f0');
    const colorPointer = token('--border', '#cbd5e1');
    const tickSet = new Set(xTickIndices);
    const markLineData: Array<{ yAxis: number; lineStyle: { color: string; type: 'dashed' }; label: { formatter: string; position: 'insideEndTop'; color: string; fontSize: number } }> = [];
    if (derivedTarget > 0 && !isDebt) {
      markLineData.push({
        yAxis: derivedTarget,
        lineStyle: { color: colorTarget, type: 'dashed' },
        label: { formatter: 'meta', position: 'insideEndTop', color: colorTarget, fontSize: 12 },
      });
    }
    if (isDebt) {
      markLineData.push({
        yAxis: 0,
        lineStyle: { color: colorTarget, type: 'dashed' },
        label: { formatter: 'quitado', position: 'insideEndTop', color: colorTarget, fontSize: 12 },
      });
    }
    return {
      tooltip: {
        trigger: 'axis',
        confine: true,
        axisPointer: { type: 'line', lineStyle: { color: colorPointer, type: 'dashed' } },
        formatter: (raw: unknown) => {
          const arr = Array.isArray(raw) ? raw : [raw];
          const p = arr[0] as { dataIndex?: number; value?: number };
          const idx = p.dataIndex ?? -1;
          const point = data[idx];
          if (!point) return '';
          const v = typeof p.value === 'number' ? p.value : 0;
          return `<div style="min-width:140px;"><div style="font-size:11px;font-weight:600;color:#f8fafc;margin-bottom:3px;">${point.label}</div><div style="font-size:11px;color:#cbd5e1;display:flex;justify-content:space-between;gap:12px;"><span>${isDebt ? 'saldo' : 'acumulado'}</span><span style="color:#f8fafc;font-family:monospace;">${formatCurrency(v)}</span></div></div>`;
        },
      },
      grid: { left: 0, right: 16, top: 8, bottom: 4, containLabel: true },
      xAxis: {
        type: 'category',
        data: data.map((p) => p.monthIndex),
        axisLine: { lineStyle: { color: colorAxis } },
        axisTick: { show: false },
        axisLabel: {
          fontFamily: 'ui-monospace, monospace',
          margin: 8,
          hideOverlap: true,
          interval: (idx: number, val: string) => tickSet.has(Number(val)),
          formatter: (v: string) => (Number(v) === 0 ? 'hoje' : monthLabel(Number(v))),
        },
      },
      yAxis: {
        type: 'value',
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: { lineStyle: { color: 'rgba(148,163,184,0.16)', type: 'dashed' } },
        axisLabel: {
          fontFamily: 'ui-monospace, monospace',
          formatter: (v: number) => formatCompactBRL(v),
        },
      },
      series: [
        {
          type: 'line',
          smooth: true,
          showSymbol: false,
          data: data.map((p) => p.base),
          lineStyle: { color: colorLine, width: 2.25 },
          itemStyle: { color: colorLine },
          markLine: markLineData.length > 0 ? { silent: true, symbol: 'none', data: markLineData } : undefined,
        },
      ],
    };
    // themeVersion: option colours are read from CSS tokens — rebuild on a theme flip.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, xTickIndices, derivedTarget, isDebt, formatCurrency, themeVersion]);

  if (!canProject) return null;

  return (
    <>
      <div className="-mx-2 h-[200px]">
        <EChart option={option} />
      </div>

      <div className="mt-4 flex items-start gap-2 rounded-2xl bg-surface-sunken p-3">
        <SparkleIcon className="mt-0.5 size-3.5 shrink-0 text-success-text" />
        <p className="text-xs/relaxed text-text-secondary">
          {completionMonth !== null ? (
            <>
              Nesse ritmo, {isDebt ? 'quitação' : 'meta atingida'} em aproximadamente{' '}
              <span className="font-bold text-text-primary">
                {completionMonth} {completionMonth === 1 ? 'mês' : 'meses'}
              </span>
              .
            </>
          ) : (
            <>
              Em {MONTHS_FORWARD} meses não{' '}
              {isDebt ? 'quita o saldo' : 'atinge o alvo'} — aumente o{' '}
              {isDebt ? 'pagamento mensal' : 'aporte'} para acelerar.
            </>
          )}
        </p>
      </div>
    </>
  );
}

export function CreatePreview(props: Props) {
  const { goalType, name, targetAmount, monthlyContribution, startingBalance, reserveMonths, essentialMonthly, debtBalance } = props;
  const formatCurrency = useFormatCurrency();
  const isDebt = goalType === 'DebtPayoff';

  const derivedTarget = useMemo(() => {
    if (goalType === 'Reserve') return reserveMonths * essentialMonthly;
    if (goalType === 'DebtPayoff') return debtBalance;
    return targetAmount;
  }, [goalType, reserveMonths, essentialMonthly, debtBalance, targetAmount]);

  const canProject = useMemo(() => {
    if (isDebt) return Number.isFinite(debtBalance) && debtBalance > 0;
    if (goalType === 'Reserve') {
      return (reserveMonths > 0 && essentialMonthly > 0) || monthlyContribution > 0 || startingBalance > 0;
    }
    return targetAmount > 0 || monthlyContribution > 0 || startingBalance > 0;
  }, [isDebt, goalType, debtBalance, reserveMonths, essentialMonthly, monthlyContribution, targetAmount, startingBalance]);

  if (!canProject) return <HowItWorks goalType={goalType} />;

  const Icon = goalType === 'Reserve' ? ShieldIcon : isDebt ? CreditCardIcon : PiggyBankIcon;
  const iconBg = goalType === 'Reserve' ? 'bg-info-surface' : isDebt ? 'bg-error-surface' : 'bg-success-surface';
  const iconColor = goalType === 'Reserve' ? 'text-info-text' : isDebt ? 'text-error-text' : 'text-success-text';

  return (
    <div className="rounded-2xl border border-border-subtle bg-surface p-4 shadow-none">
      <div className="mb-4 flex items-start justify-between">
        <span className="text-2xs font-bold tracking-widest text-text-tertiary uppercase">
          Trajetória projetada
        </span>
        <div className={`size-10 rounded-xl flex items-center justify-center ${iconBg} ${iconColor}`}>
          <Icon className="size-5" weight="bold" />
        </div>
      </div>

      <div className="mb-4">
        <h2 className="mb-1 text-lg font-bold wrap-break-word text-text-primary">
          {name || (goalType === 'Reserve' ? 'Reserva de Emergência' : 'Sua nova meta')}
        </h2>
        <div className="flex items-baseline gap-2 text-sm">
          <span className="text-text-tertiary">{isDebt ? 'Saldo devedor' : 'Valor alvo'}</span>
          <span className="font-mono text-lg font-bold text-text-primary tabular-nums">
            {formatCurrency(derivedTarget)}
          </span>
        </div>
        {startingBalance > 0 && !isDebt && (
          <p className="mt-1 text-xs text-text-tertiary">
            Acumulado inicial:{' '}
            <span className="font-mono text-text-primary">{formatCurrency(startingBalance)}</span>
          </p>
        )}
      </div>

      <ProjectionInline {...props} />
    </div>
  );
}
